import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

interface TopHintProps {
    isVisible: boolean;
    message: string | React.ReactNode;
    onClose?: () => void;
    onSubmit?: () => void;
    showCloseButton?: boolean;
    theme?: "light" | "dark";
    className?: string;
    centered?: boolean;
    messageContainerClassName?: string;
}

export const TopHint: React.FC<TopHintProps> = ({
    isVisible,
    message,
    onClose,
    onSubmit,
    showCloseButton = true,
    theme = "dark",
    centered = true,
    className = "",
    messageContainerClassName = ""
}) => {
    // Theme-based styling
    const themeStyles = {
        light: {
            bg: "bg-white",
            text: "text-primary",
            closeButton: "text-gray-500 hover:text-gray-600"
        },
        dark: {
            bg: "bg-gray-800",
            text: "text-white",
            closeButton: "text-gray-300 hover:text-gray-200"
        }
    };

    const currentTheme = themeStyles[theme];

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // Render to portal to bypass any parent overflow/stacking issues
    return createPortal(
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300, duration: 0.3 }}
                    className={`fixed left-0 right-0 top-0 z-[9999] h-14 ${currentTheme.bg} shadow-lg ${className}`}
                >
                    <div className="flex h-full items-center justify-between px-4">
                        {/* Left close button */}
                        {showCloseButton && (
                            <button
                                onClick={handleClose}
                                className={`${currentTheme.closeButton} transition-colors`}
                                aria-label="Close"
                            >
                                <i className="far fa-times" />
                            </button>
                        )}
                        {!showCloseButton && centered && <div />}{" "}
                        {/* Spacer when no close button */}
                        {/* Centered message */}
                        <div
                            className={twClassNames(
                                "flex flex-1",
                                centered && "justify-center",
                                messageContainerClassName
                            )}
                        >
                            {typeof message === "string" ? (
                                <p className={`text-sm font-medium ${currentTheme.text}`}>
                                    {message}
                                </p>
                            ) : (
                                message
                            )}
                        </div>
                        {/* Right submit button */}
                        {onSubmit && (
                            <button
                                onClick={onSubmit}
                                className={`${currentTheme.closeButton} transition-colors`}
                            >
                                <i className="far fa-check" />
                            </button>
                        )}
                        {/* Right spacer to keep message centered */}
                        {!onSubmit && !showCloseButton && centered && <div className="w-6" />}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};
